import { create } from 'zustand';
import { tokenStorage } from './storage';

export type Clinician = {
  id?: number;
  hospital_ids?: number[];
  hospital_id?: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  flags?: Record<string, unknown>;
  locale?: string;
  /** Internal RxFood staff flag. Drives visibility of admin-only UI like
   *  the patient-list "Draft report available" chip — v1 gates that chip
   *  on the same flag (clinician/src/pages/patientlist.tsx:268). */
  is_internal?: boolean;
  [k: string]: unknown;
};

type State = {
  token: string | null;
  clinician: Clinician | null;
  pendingCredentials: { username: string; password: string } | null;
  setToken: (token: string | null) => void;
  setClinician: (clinician: Clinician | null) => void;
  setPendingCredentials: (creds: { username: string; password: string } | null) => void;
  logout: () => Promise<void>;
};

const FLAG_OVERRIDE_KEY = 'dev.flagOverrides';

/**
 * Read dev-only flag overrides from localStorage. Production builds skip
 * this entirely (see `import.meta.env.DEV` gate). The overrides are merged
 * on top of whatever flags the server returns, so a clinician with
 * `clinician_allow_meal_edit: false` from the API can still test the
 * meal-edit UI locally.
 */
const readFlagOverrides = (): Record<string, unknown> => {
  if (!import.meta.env.DEV) return {};
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(FLAG_OVERRIDE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const applyFlagOverrides = (clinician: Clinician | null): Clinician | null => {
  if (!clinician) return clinician;
  const overrides = readFlagOverrides();
  if (Object.keys(overrides).length === 0) return clinician;
  return {
    ...clinician,
    flags: {
      ...((clinician.flags as Record<string, unknown> | undefined) ?? {}),
      ...overrides,
    },
  };
};

export const useAuthStore = create<State>((set) => ({
  token: tokenStorage.read(),
  clinician: null,
  pendingCredentials: null,

  setToken: (token) => {
    if (token) tokenStorage.write(token);
    else tokenStorage.clear();
    set({ token });
  },

  setClinician: (clinician) => set({ clinician: applyFlagOverrides(clinician) }),

  setPendingCredentials: (creds) => set({ pendingCredentials: creds }),

  logout: async () => {
    // Fire-and-forget server-side revocation; the local clear is what actually
    // matters for the user experience.
    try {
      const { authApi } = await import('./api');
      await authApi.logout().catch(() => undefined);
    } catch {
      // ignore
    }
    tokenStorage.clear();
    set({ token: null, clinician: null, pendingCredentials: null });
  },
}));
